import React from "react";
import { Atom, Menu, X, Globe, Sun, Moon } from "lucide-react";

import { getText } from "../../data/content";
import { theme, combineClasses } from "../../theme/theme";
import { UniversityLogo } from "../Logos";
import NavButton from "./NavButton";

/**
 * ============================================================================
 * ############## SiteNavbar：全站导航栏 ########################################
 * ============================================================================
 *
 * 【作用】
 * - 渲染全站顶部导航：Logo 区、桌面导航项、桌面控制区（主题/语言/Contact）、移动端控制区与下拉菜单。
 * - 所有状态与事件由父组件 App 通过 props 下发。
 *
 * 【如何修改】
 *
 * 【响应式行为】
 * - 展开模式（≥lg，1024px）：中间为整行导航链接，右侧为主题/语言/联系我们；导航项之间的间距随视口变大而变宽（见下）。
 * - 缩略模式（<lg）：隐藏整行导航与右侧大按钮，显示汉堡菜单；点击后展开下拉，内含所有页面链接与联系我们。
 *
 * 【如何修改】
 *
 * 1) 导航词之间的间距（随页面大小变化，不写死）
 *    - 在 “NAV CENTER” 使用响应式 gap：gap-x-1 lg:gap-x-2 xl:gap-x-3 2xl:gap-x-4。小屏更紧、大屏更宽。
 *    - 可按需改断点或数值；若要用视口比例可试 gap-x-[clamp(0.25rem,1.5vw,1.5rem)]（需 Tailwind 或内联 style）。
 *
 * 2) 何时显示“缩略”（下拉） vs “展开”（整行导航）
 *    - 断点统一为 lg（1024px）。<1024px 显示汉堡与下拉，≥1024px 显示整行导航。四处置为同一断点即可。
 *
 * 3) “联系我们”和“EN/CN”按钮大小（已还原为较大尺寸）
 *    - 桌面端“联系我们”：px-5 py-2、text-xs。桌面端“EN/CN”和“主题”：px-3 py-1、text-xs。改对应注释处 class 即可。
 *    - 移动端与下拉内 Contact：见 “MOBILE CONTROLS” 与下拉内按钮。
 */
export interface SiteNavbarProps {
  lang: "en" | "cn";
  themeMode: "light" | "dark";

  isMenuOpen: boolean;
  activePage: string;
  navPages: readonly string[];

  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onNavigate: (page: string) => void;

  onToggleTheme: () => void;
  onToggleLang: () => void;
  onOpenContact: () => void;
}

const SiteNavbar: React.FC<SiteNavbarProps> = ({
  lang,
  themeMode,
  isMenuOpen,
  activePage,
  navPages,
  onToggleMenu,
  onCloseMenu,
  onNavigate,
  onToggleTheme,
  onToggleLang,
  onOpenContact
}) => {
  const isLight = themeMode === "light";

  return (
    <nav
      className={combineClasses(
        isLight ? theme.navLight.bar : theme.nav.bar,
        "pointer-events-auto"
      )}
    >
      <div className={theme.nav.container}>
        <div
          className={combineClasses(
            "flex items-center justify-between",
            theme.nav.height,
            "pointer-events-auto",
            // [关键修复] 禁止换行并允许中间区域收缩，避免语言差异导致的布局抖动
            "flex-nowrap min-w-0"
          )}
        >
          {/* ############################################################################
              ############## NAV LEFT（Logo 区） #####################################
              ############################################################################ */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* 学校Logo（超链接） */}
            <a
              href="https://www.sustech.edu.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              style={{ lineHeight: 0 }} // 去除环绕间隙，更紧凑
            >
              <span className="inline-block" style={{ fontSize: 0 }}>
                <span className="scale-125 block">
                  <UniversityLogo />
                </span>
              </span>
            </a>

            {/* QLab Logo（点击回到首页，图片方案） */}
            <div
              className={isLight ? theme.navLight.logo.container : theme.nav.logo.container}
              onClick={() => onNavigate("home")}
              style={{ cursor: "pointer" }}
            >
              <div className="relative">
                <div className={isLight ? theme.navLight.logo.glow : theme.nav.logo.glow}></div>
                <img
                src={isLight 
                  ? "/images/logos/qlab-logo-light.png"
                  : "/images/logos/qlab-logo-dark.png"}
                  alt="Qlab Logo"
                  className={combineClasses(
                    "w-12 h-12 object-contain",
                    isLight ? theme.navLight.logo.icon : theme.nav.logo.icon,
                    isLight ? "bg-gray-100" : ""
                  )}
                  draggable="false"
                  style={{ display: "block" }}
                />
              </div>
              <span className={combineClasses("text-xl font-bold tracking-tighter", isLight ? theme.navLight.logo.text : theme.nav.logo.text)}>
                Q-Lab
              </span>
            </div>
          </div>

          {/* ############################################################################
              ############## NAV CENTER（桌面导航项）
              - 间距随视口变化（不写死）：gap-x-1（lg）→ gap-x-2（xl）→ gap-x-3（2xl），
                小屏更紧、大屏更宽。修改断点或数值见文件头【如何修改】1)。
              - hidden lg:flex：≥1024px 显示整行导航；<1024px 时由 MOBILE 区显示汉堡与下拉。
              ############################################################################ */}
          <div
            className={combineClasses(
              "hidden lg:flex items-center flex-nowrap min-w-0 overflow-hidden",
              "gap-x-1 lg:gap-x-2 xl:gap-x-3 2xl:gap-x-4"
            )}
          >
            {navPages.map((page) => (
              <div key={page} className="flex-shrink-0">
                <NavButton
                  label={getText(`nav.${page}`, lang)}
                  isActive={activePage === page}
                  onClick={() => {
                    onNavigate(page);
                    onCloseMenu();
                  }}
                  themeMode={themeMode}
                />
              </div>
            ))}
          </div>

          {/* ############################################################################
              ############## NAV RIGHT（桌面控制区）
              - hidden lg:flex：与 NAV CENTER 一致，≥1024px 显示；<1024px 时隐藏，由 MOBILE 区接管。
              - 联系我们 / EN/CN / 主题：已还原为较大尺寸（px-5 py-2 Contact，px-3 py-1 语言与主题）。见文件头【如何修改】3)。
              ############################################################################ */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            {/* 主题切换 */}
            <button
              onClick={onToggleTheme}
              className={`px-3 py-1 rounded-md border text-xs font-bold transition-all flex items-center gap-2 ${
                isLight ? "border-gray-300 text-gray-700 hover:bg-gray-50" : "border-white/20 text-gray-300 hover:bg-white/10"
              }`}
              title={
                isLight
                  ? (lang === "cn" ? "切换到暗色模式" : "Switch to dark mode")
                  : (lang === "cn" ? "切换到亮色模式" : "Switch to light mode")
              }
            >
              {isLight ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
            </button>

            {/* 语言切换 */}
            <button
              onClick={onToggleLang}
              className={`px-3 py-1 rounded-md border text-xs font-bold transition-all flex items-center gap-2 ${
                isLight ? "border-gray-300 text-gray-700 hover:bg-gray-50" : "border-white/20 text-gray-300 hover:bg-white/10"
              }`}
            >
              <Globe className="w-3 h-3" />
              {lang === "en" ? "中" : "EN"}
            </button>

            {/* Contact：桌面端（还原为较大尺寸） */}
            <div className={`ml-4 pl-4 border-l ${isLight ? "border-gray-200" : "border-white/10"}`}>
              <button
                onClick={onOpenContact}
                className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  isLight
                    ? "bg-gray-900 text-white hover:bg-gray-800 shadow-lg"
                    : "bg-white text-black hover:bg-cyan-300 shadow-[0_0_15px_rgba(255,255,255,0.2)]"
                }`}
              >
                {getText("nav.contact", lang)}
              </button>
            </div>
          </div>

          {/* ############################################################################
              ############## MOBILE CONTROLS（移动端按钮区）
              - lg:hidden：<1024px 显示；此时整行导航与右侧大按钮隐藏，仅保留主题/语言/汉堡。
              缩到现在尺寸时即切换为下拉缩略。断点与 NAV CENTER / NAV RIGHT 的 lg 一致。
              ############################################################################ */}
          <div className="lg:hidden flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onToggleTheme}
              className={`p-2 rounded-md border ${isLight ? "border-gray-300 text-gray-700" : "border-white/20 text-white"}`}
              aria-label={lang === "cn" ? "切换主题" : "Toggle theme"}
            >
              {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>

            <button
              onClick={onToggleLang}
              className={`text-xs font-bold border px-2 py-1 rounded ${isLight ? "border-gray-300 text-gray-700" : "border-white/20 text-white"}`}
              aria-label={lang === "cn" ? "切换语言" : "Toggle language"}
            >
              {lang === "en" ? "中" : "EN"}
            </button>

            <button
              onClick={onToggleMenu}
              className={combineClasses(isLight ? "text-gray-900" : "text-white", "p-2")}
              aria-label={isMenuOpen ? (lang === "cn" ? "关闭菜单" : "Close menu") : (lang === "cn" ? "打开菜单" : "Open menu")}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* ############################################################################
          ############## MOBILE DROPDOWN（移动端下拉菜单）
          - lg:hidden：与 MOBILE CONTROLS 一致，仅在 <1024px 时渲染。
          ############################################################################ */}
      {isMenuOpen && (
        <div
          className={combineClasses(
            "lg:hidden border-b absolute w-full z-50",
            isLight ? "bg-white border-gray-200" : "bg-black border-white/10"
          )}
        >
          <div className="px-4 pt-2 pb-6 space-y-1">
            {navPages.map((page) => (
              <button
                key={page}
                onClick={() => {
                  onNavigate(page);
                  onCloseMenu();
                }}
                className={`block w-full text-left px-3 py-4 text-lg font-bold capitalize ${
                  isLight ? "text-gray-900 border-b border-gray-200" : "text-white border-b border-white/5"
                }`}
              >
                {getText(`nav.${page}`, lang)}
              </button>
            ))}

            <button
              onClick={() => {
                onOpenContact();
                onCloseMenu();
              }}
              className={`block w-full text-center px-3 py-4 mt-3 rounded-full text-sm font-bold uppercase tracking-wider transition-colors ${
                isLight ? "bg-gray-900 text-white hover:bg-gray-800" : "bg-white text-black hover:bg-cyan-300"
              }`}
            >
              {getText("nav.contact", lang)}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default SiteNavbar;
